import React, { useState, useRef, useEffect, FC } from 'react';
import { Project as ProjectModel } from '@material-bank/projects-api';
import { CustomIcon } from 'components/CustomIcon';
import useSWRMutation from 'swr/mutation';
import { MAX_PROJECTS, API } from '../config';
import {
  getCurrentProjectData,
  currentProjectUpdate,
  getUserOrderType,
} from '../utilities';
import * as Styled from './ChangeProjectDropDown.styles';

interface ChangeProjectDropDownProps {
  projects: Record<string, ProjectModel>;
  showSelectedProject?: boolean;
  currentProject: ProjectModel | null;
  onProjectChange?: (projectId: string) => void;
  openDirection?: 'bottom' | 'top'; // The direction the dropdown opens relative to the button
}

export const ChangeProjectDropDown: FC<ChangeProjectDropDownProps> = ({
  projects,
  showSelectedProject = false,
  openDirection = 'bottom',
  currentProject,
  onProjectChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [shouldUpdate, setShouldUpdate] = useState(false);

  const { isNonSampleUser } = getUserOrderType();

  const { trigger, data, error } = useSWRMutation(
    API.projectSetList,
    currentProjectUpdate,
    {
      revalidate: false,
    },
  );

  const handleProjectClick = (projectId: string) => {
    setIsOpen(false);
    setShouldUpdate(true);
    trigger({
      project_id: parseInt(projectId, 10),
    });

    const selectedProject = projects[projectId];

    if (onProjectChange && selectedProject) {
      onProjectChange(selectedProject.externalId.toString());
    }

    dataLayer.push({
      event: 'hpauth_change_proj',
      current_proj_id: currentProject?.externalId,
      updated_proj_id: projectId,
      interaction_location: 'projects_header',
    });
  };

  useEffect(() => {
    if (data?.status === 'success' && shouldUpdate) {
      window.dispatchEvent(new CustomEvent('reload-customer-section-data'));
    }

    setShouldUpdate(false);
  }, [data, error, shouldUpdate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        // TODO: validate the intention of the code and fix the type
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        !panelRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleCreateNewProject = () => {
    window.dispatchEvent(
      new CustomEvent('open-create-project-modal', {
        detail: {
          showModalAfterCreate: false,
          eventLocation: 'Homepage',
        },
      }),
    );
    setIsOpen(false);

    const projectData = getCurrentProjectData();

    dataLayer.push({
      event: isNonSampleUser
        ? 'hpauthnonsampling_create_new_proj'
        : 'hpauth_create_new_proj',
      proj_creation_method: 'change_proj_cta',
      project_id: projectData?.projectId,
      project_name: projectData?.projectId,
      interaction_location: 'projects_header',
    });
  };

  return (
    <div className="relative">
      <Styled.ToggleButton onClick={handleToggle} type="button" isOpen={isOpen}>
        {showSelectedProject ? (
          <Styled.ProjectTextWrapper>
            <Styled.ProjectText>Project:</Styled.ProjectText>{' '}
            {currentProject?.title}
          </Styled.ProjectTextWrapper>
        ) : (
          <span>Change project</span>
        )}{' '}
        <CustomIcon name={!isOpen ? 'arrow-down' : 'arrow-up'} />
      </Styled.ToggleButton>
      {isOpen ? (
        <Styled.PanelWrapper openDirection={openDirection} ref={panelRef}>
          <Styled.ProjectList>
            {Object.values(projects).map((project, index) =>
              index < MAX_PROJECTS ? (
                <li key={project.externalId}>
                  <Styled.ExistingProjectButton
                    isCurrentProject={
                      project.externalId === currentProject?.externalId
                    }
                    onClick={() => {
                      handleProjectClick(project.externalId.toString());
                    }}
                  >
                    {project.title}
                  </Styled.ExistingProjectButton>
                </li>
              ) : null,
            )}
          </Styled.ProjectList>
          <Styled.CreateProjectButton
            onClick={handleCreateNewProject}
            type="button"
          >
            <CustomIcon name={'add'} /> Create a new project
          </Styled.CreateProjectButton>
        </Styled.PanelWrapper>
      ) : null}
    </div>
  );
};
